import { 
  DURATION_LABELS_SOURCE,
  DURATION_LABELS_LAYER
} from './constants';
import { 
  calculateMidpoint, 
  safeSetSourceData
} from './utils';

/**
 * Generate duration labels for connection lines
 * @param {Array} connections - Array of connection objects
 * @returns {Array} Array of GeoJSON features for duration labels
 */
export const generateDurationLabels = (connections) => {
  if (!connections || connections.length === 0) return [];
  
  const features = [];
  
  connections.forEach(connection => {
    // Only show labels for connections with duration
    if (!connection.duration || !connection.points || connection.points.length !== 2) {
      return;
    }
    
    const [point1, point2] = connection.points;
    
    // Validate point data
    if (!point1 || !point2 || 
        typeof point1.latitude !== 'number' || 
        typeof point1.longitude !== 'number' ||
        typeof point2.latitude !== 'number' || 
        typeof point2.longitude !== 'number' ||
        isNaN(point1.latitude) || isNaN(point1.longitude) ||
        isNaN(point2.latitude) || isNaN(point2.longitude)) {
      return;
    }
    
    // Calculate midpoint for label placement
    const midpoint = calculateMidpoint(
      [point1.longitude, point1.latitude],
      [point2.longitude, point2.latitude]
    );
    
    // Create label feature
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: midpoint
      },
      properties: {
        duration: `${connection.duration}h`,
        transportMode: connection.transportMode,
        connectionId: connection.id
        // Remove color property to prevent style conflicts
      }
    });
  });
  
  return features;
};

/**
 * Initialize duration labels layer
 * @param {Object} map - Mapbox map instance
 */
export const initializeDurationLabelsLayer = (map) => {
  if (!map) {
    return;
  }
  
  // Wait for map to be ready if it's not loaded yet
  if (!map.loaded()) {
    // Use once to avoid duplicate event listeners
    map.once('load', () => {
      initializeDurationLabelsLayer(map);
    });
    return;
  }

  try {
    // Add duration labels data source
    if (!map.getSource(DURATION_LABELS_SOURCE)) {
      map.addSource(DURATION_LABELS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
    }

    // Remove existing layer if it exists to ensure clean recreation
    if (map.getLayer(DURATION_LABELS_LAYER)) {
      map.removeLayer(DURATION_LABELS_LAYER);
    }
    
    // Always add fresh layer with correct styling
    map.addLayer({
      id: DURATION_LABELS_LAYER,
      type: 'symbol',
      source: DURATION_LABELS_SOURCE,
      layout: {
        'text-field': ['get', 'duration'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 16,
        'text-anchor': 'center',
        'text-offset': [0, 0],
        'text-allow-overlap': false,
        'text-ignore-placement': false
      },
      paint: {
        'text-color': '#000000',
        'text-halo-width': 0  // Ensure no halo that might use color property
      }
    });
  } catch (error) {
    console.error('Error initializing duration labels layer:', error);
  }
};

/**
 * Update duration labels on map
 * @param {Object} params - Parameters object
 * @param {Object} params.map - Mapbox map instance
 * @param {boolean} params.mapReady - Whether map is ready
 * @param {boolean} params.sourcesReady - Whether data sources are ready
 * @param {Array} params.connections - Array of connections
 * @param {boolean} params.shouldShowDurationLabels - Whether to show duration labels
 */
export const updateDurationLabels = ({
  map,
  mapReady,
  sourcesReady,
  connections,
  shouldShowDurationLabels
}) => {
  if (!map || !mapReady || !sourcesReady) {
    return;
  }

  try {
    // Ensure duration labels layer is initialized
    initializeDurationLabelsLayer(map);

    if (!shouldShowDurationLabels || !connections || connections.length === 0) {
      // Clear duration labels
      safeSetSourceData(map, mapReady, sourcesReady, DURATION_LABELS_SOURCE, {
        type: 'FeatureCollection',
        features: []
      });
      return;
    }

    // Generate duration label features
    const durationFeatures = generateDurationLabels(connections);

    // Create GeoJSON data
    const geoJsonData = {
      type: 'FeatureCollection',
      features: durationFeatures
    };

    // Update data source
    safeSetSourceData(map, mapReady, sourcesReady, DURATION_LABELS_SOURCE, geoJsonData);

  } catch (error) {
    console.error('Error updating duration labels:', error);
  }
};

/**
 * Set duration labels visibility
 * @param {Object} map - Mapbox map instance
 * @param {boolean} mapReady - Whether map is ready
 * @param {boolean} shouldShow - Whether to show duration labels
 */
export const setDurationLabelsVisibility = (map, mapReady, shouldShow) => {
  if (!map || !mapReady) return;
  
  try {
    if (map.getLayer(DURATION_LABELS_LAYER)) {
      map.setLayoutProperty(DURATION_LABELS_LAYER, 'visibility', shouldShow ? 'visible' : 'none');
    }
  } catch (error) {
    console.error('Error setting duration labels visibility:', error);
  }
};

